"use client";
import styles from "./Navbar.module.scss";
import { MenuType, PlatformType } from "../../types/baseTypes";
import { OptionsHeaderType } from "@/types/OptionType";
import { NavigationOptionsType } from "@/types/ModuleOptionsType";
import { Logos } from "./Logos/Logos";
import { Menu } from "./Menu/Menu";
import { Socials } from "./Socials/Socials";
import { LanguageToggle } from "./Language/LanguageToggle";
import { Hamburger } from "./Hamburger/Hamburger";
import { useState, useEffect } from "react";
import { useAppContext } from "../../context/AppContext";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import SearchMenuItem from "./Search/SearchMenuItem";
import useWindowSize from "@/hooks/useWindowSize";

interface NavbarProps {
  menu: MenuType;
  searchPageSlug: string;
  socialMedia: PlatformType;
  navbarOption: NavbarOptionType;
  pageData?: Post;
  isHome: boolean;
}

export type NavbarOptionType = {
  themeOption: OptionsHeaderType;
  moduleOption: NavigationOptionsType;
};

export const Navbar = ({
  navbarOption,
  searchPageSlug,
  menu,
  socialMedia,
  pageData,
  isHome,
}: NavbarProps) => {
  const { logos, logos_header_mobile } = navbarOption.themeOption;
  const {
    show_box_shadow,
    show_social_icons_in_menu,
    show_search,
    show_language,
    show_background,
  } = navbarOption.moduleOption.header_menu;
  const [hasBackground, setHasBackground] = useState<boolean>(false);
  const { openMenu } = useAppContext();
  const { width } = useWindowSize();
  const navbarBreakpointPx = parseInt(styles.navbarBreakpoint, 10);

  const isMobileNavigation = width > 0 && width < navbarBreakpointPx;

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      setHasBackground(scrollY > 20);
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  // console.log(hasBackground);
  return (
    <header>
      <nav
        data-has-bg={isHome && !show_background ? hasBackground : true}
        data-show-shadow={show_box_shadow}
        //! please dont remove scrollbar-width class
        className={`${styles["navbar"]} ${openMenu ? styles["opened"] : ""} navbar scrollbar-width`}
      >
        <Logos
          logos={logos}
          logos_mobile={logos_header_mobile}
          hasBackground={isHome && !show_background ? hasBackground : true}
          isMobile={isMobileNavigation}
        />
        <Hamburger />
        <FocusTrap disabled={!openMenu}>
          <div
            id="navbar-main"
            data-show-search={show_search}
            data-lenis-prevent
            className={`${styles["navbar-main"]} ${openMenu ? styles["opened"] : ""}`}
          >
            <div className={styles["navbar-main-mobile-scroll"]}>
              {show_search && isMobileNavigation && (
                <SearchMenuItem
                  searchPageSlug={searchPageSlug}
                  isMobileNavigation={isMobileNavigation}
                />
              )}
              <Menu menu={menu} pageData={pageData} />
              {show_social_icons_in_menu && (
                <Socials
                  platforms={socialMedia}
                  hasBackground={
                    isHome && !show_background ? hasBackground : true
                  }
                />
              )}
              {show_language && pageData && (
                <LanguageToggle addWrapper={true} post={pageData} />
              )}
              {show_search && !isMobileNavigation && (
                <SearchMenuItem
                  searchPageSlug={searchPageSlug}
                  isMobileNavigation={isMobileNavigation}
                  hasBackground={
                    isHome && !show_background ? hasBackground : true
                  }
                />
              )}
            </div>
          </div>
        </FocusTrap>
      </nav>
    </header>
  );
};
